/**
 * useTileAuth.ts — fetch + auto-refresh the gated-tile signed URLs (T-102).
 *
 * The aggregate map tiers (state / county) are served as private PMTiles from CloudFront, gated
 * by a signed URL minted per session at GET /api/tile-auth. This hook fetches those URLs once on
 * mount and re-fetches shortly before they expire so a long-lived session never hands MapLibre an
 * expired (403) tile URL. When tile delivery is unavailable (503 → distribution not wired or the
 * signing key is missing) it resolves to null, and MapShell falls back to the bbox-GeoJSON tiers.
 *
 * Signed URLs are the transport (not cookies) because the app and the CDN sit on different
 * registrable domains over http — see the T-102 topology memo.
 */
import { useEffect, useState } from 'react'
import { fetchTileAuth } from '../lib/api'

export interface TileUrls {
  state: string
  county: string
}

export interface UseTileAuthResult {
  /** Per-tier signed pmtiles URLs, or null while loading / when delivery is unavailable. */
  tiles: TileUrls | null
  loading: boolean
}

// Refresh this many seconds before the URL's stated expiry, so an in-flight tile request never
// races the expiry boundary. Comfortably inside the ~12 h TTL.
const REFRESH_SKEW_SECONDS = 300

export function useTileAuth(): UseTileAuthResult {
  const [tiles, setTiles] = useState<TileUrls | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    async function load(): Promise<void> {
      const auth = await fetchTileAuth()
      if (cancelled) return
      setLoading(false)
      if (!auth) {
        // Unavailable — MapShell keeps the bbox-GeoJSON tiers. No refresh scheduled.
        setTiles(null)
        return
      }
      setTiles(auth.tiles)
      const nowS = Math.floor(Date.now() / 1000)
      const delayMs = Math.max(0, (auth.expiresAt - nowS - REFRESH_SKEW_SECONDS) * 1000)
      timer = setTimeout(() => {
        void load()
      }, delayMs)
    }

    void load()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [])

  return { tiles, loading }
}
